import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CContainer, CSpinner, CRow, CCol, CCard, CCardHeader, CCardBody, CFormSelect
} from '@coreui/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { apiClient } from 'services/Api';

const surveyFields = [
  ['q1',  'P1 - Os exames de saúde realizados no âmbito da Medicina do Trabalho são suficientes e adequados ao seu posto de trabalho?'],
  ['q2',  'P2 - A informação e formação que dispõe sobre os riscos a que está exposto e sobre as medidas de prevenção a adotar no seu posto de trabalho é suficiente e adequada?'],
  ['q3',  'P3 - A sinalização de segurança existente (perigo, emergência, obrigação, informação, etc) é suficiente e adequada?'],
  ['q4',  'P4 - Os locais de circulação (escadas, portas, pavimentos, corredores) apresentam as devidas condições de segurança?'],
  ['q5',  'P5 - Existem implementadas medidas de combate a incêndio, primeiros socorros e evacuação para responder a uma situação de emergência?'],
  ['q6',  'P6 - As medidas de combate a incêndio, primeiros socorros e evacuação para responder a uma situação de emergência são adequadas?'],
  ['q7',  'P7 - Está exposto a riscos especiais (amianto, espaços confinados, etc)?'],
  ['q8',  'P8 - Está exposto a agentes físicos (ruído, vibrações, radiações, iluminação inadequada, etc)?'],
  ['q9',  'P9 - Está exposto a agentes químicos (poeiras, gases, vapores, fumos, cheiros, etc)?'],
  ['q10', 'P10 - Está exposto a riscos ergonómicos (movimentação manual de cargas, sobre-esforços, movimentos repetitivos, posturas inadequadas, fadiga visual, etc)?'],
  ['q11', 'P11 - Está exposto a riscos biológicos (vírus, bactérias, fungos, etc)?'],
  ['q12', 'P12 - As medidas de prevenção e de proteção já colocadas em prática no seu posto de trabalho são adequadas?'],
  ['q13', 'P13 - Existem medidas de prevenção e de proteção que devam ser postas em prática no seu posto de trabalho?'],
  ['q14', 'P14 - Tem à sua disposição os equipamentos de proteção individual de acordo com os riscos a que está exposto no seu posto de trabalho?'],
];

const buildYearOptions = (start = 2019) => {
  const current = new Date().getFullYear() - 1;
  const list = [];
  for (let y = start; y <= current; y++) list.push(y);
  return list.reverse();
};

const SstSurveyResults = () => {
  const { year: paramYear } = useParams();
  const navigate = useNavigate();
  const currentYear = (new Date().getFullYear() - 1).toString();

  const [data, setData] = useState(null);           // { year, counts: { q1: {true, false}, ...}, total_responses }
  const [loading, setLoading] = useState(true);
  const [yearList, setYearList] = useState([]);
  const [year, setYear] = useState(paramYear || currentYear);

  const questionCodes = useMemo(() => surveyFields.map(([key]) => key), []);
  const questionLabels = useMemo(() => surveyFields.map(([, label]) => label), []);

  const simSeries = useMemo(() => {
    if (!data?.counts) return surveyFields.map(() => 0);
    return surveyFields.map(([key]) => Number(data.counts?.[key]?.true || 0));
  }, [data]);

  const naoSeries = useMemo(() => {
    if (!data?.counts) return surveyFields.map(() => 0);
    return surveyFields.map(([key]) => Number(data.counts?.[key]?.false || 0));
  }, [data]);

  const totalPerQuestion = useMemo(
    () => simSeries.map((v, i) => v + (naoSeries[i] || 0)),
    [simSeries, naoSeries]
  );

  const generateChartOptions = () => {
    return {
      chart: { type: 'column', height: 420 },
      title: { text: null },
      xAxis: {
        categories: surveyFields.map((_, i) => `P${i + 1}`),
        crosshair: true,
        title: { text: 'Perguntas' },
      },
      yAxis: {
        min: 0,
        title: { text: 'Respostas (contagem)' },
        allowDecimals: false,
        stackLabels: { enabled: true } // optional, shows totals above bars
      },
      legend: { align: 'center', verticalAlign: 'bottom' },
      tooltip: {
        shared: true,
        formatter: function () {
          const idx = this.points?.[0]?.point?.index ?? 0;
          const total = totalPerQuestion[idx] || 0;
          const rows = this.points.map(p => {
            const pct = total ? ((p.y / total) * 100).toFixed(0) : 0;
            return `<span style="font-weight:600;">${p.series.name}:</span> ${p.y} (${pct}%)`;
          }).join('<br/>');
          return `<b>P${idx + 1}</b><br/>${rows}`;
        }
      },
      plotOptions: {
        column: {
          pointPadding: 0.1,
          borderWidth: 0,
          dataLabels: { enabled: false }
        },
        series: {
          stacking: 'normal' // <--- makes bars stack
        }
      },
      series: [
        { name: 'Sim', data: simSeries, color: '#28a745' }, // green
        { name: 'Não', data: naoSeries, color: '#dc3545' }  // red
      ],
      credits: { enabled: false },
    };
  };  

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const selectedYear = paramYear || currentYear;
        const res = await apiClient.get(`/sst-surveys/results/${selectedYear}/`);
        // If your API also returns available_years, use it; otherwise fallback to 2019..current
        setYearList(res.data.available_years || buildYearOptions(2019));
        setData(res.data);
        setYear(selectedYear);
      } catch (err) {
        console.error('Erro ao carregar resultados SST:', err);
        setData(null);
        setYearList(buildYearOptions(2019));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [paramYear, currentYear]);

  if (loading || !data) {
    return (
      <CContainer className="text-center py-5">
        <CSpinner color="primary" />
      </CContainer>
    );
  }

  return (
    <CContainer className="py-4">
      <h3 className="mb-4">Resultados — Inquérito SST</h3>

      <CRow className="mb-4">
        <CCol md={12}>
          <CFormSelect
            value={year}
            onChange={(e) => navigate(`/inqueritos-sst/resultados/${e.target.value}`)}
          >
            {yearList.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </CFormSelect>
        </CCol>
      </CRow>

      <CRow className="mb-4">
        <CCol md={6}>
          <CCard>
            <CCardHeader><strong>Segurança e Saúde no Trabalho</strong></CCardHeader>
            <CCardBody>
              <HighchartsReact highcharts={Highcharts} options={generateChartOptions()} />
            </CCardBody>
          </CCard>
        </CCol>

        <CCol md={6}>
          <CCard className="h-100">
            <CCardHeader><strong>Segurança e Saúde no Trabalho</strong></CCardHeader>
            <CCardBody>
              <ul className="mb-0" style={{ paddingLeft: '1rem' }}>
                {questionLabels.map((q, idx) => (
                  <li key={idx} className="mb-2">{q}</li>
                ))}
              </ul>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </CContainer>
  );
};

export default SstSurveyResults;
